import moduleStyle from './index.module.less';

import { Button, ButtonProps, Flex, Space, Spinner } from '@tonic-one/react';
import React, { useRef, useState } from 'react';

interface LoadingBtnProps {
  disabled?: boolean;
  /**the children can be the button text */
  children: React.ReactNode | ((loading: boolean) => React.ReactNode);
  /**the onClick should have try catch inner and correct show notification */
  onClick: () => Promise<void>;
  /**controlled loading state from the parent (e.g. an in-flight async op).
   *  When true the button is disabled and shows the spinner. */
  loading?: boolean;
  icon?: JSX.Element;
  variant?: ButtonProps['variant'];
  style?: Object;
  [key: string]: any;
}

export default function LoadingBtn({
  disabled = false,
  loading = false,
  children,
  onClick,
  icon,
  variant = 'primary',
  style = {},
  ...rest
}: LoadingBtnProps) {
  const [internalLoading, setInternalLoading] = useState(false);
  // Synchronous re-entry latch. React state is not committed until the next
  // render, so a fast double-click could read `loading === false` twice and
  // fire concurrent submits. A ref is updated immediately, closing that race.
  const submittingRef = useRef(false);

  const isBusy = loading || internalLoading;

  const classes = isBusy
    ? `${moduleStyle.loadingBtn} ${moduleStyle.loading}`
    : moduleStyle.loadingBtn;

  const _onClick = async () => {
    if (loading || submittingRef.current) return;
    submittingRef.current = true;
    setInternalLoading(true);
    try {
      await onClick();
    } catch (e) {
      console.error(e);
    } finally {
      submittingRef.current = false;
      setInternalLoading(false);
    }
  };

  return (
    <div style={{ position: 'relative' }} onClick={_onClick}>
      <div className={moduleStyle.icon}>
        {isBusy && <Spinner size="xs" lineColor="#1E5EDE" />}
        {!isBusy && icon && <Flex>{icon}</Flex>}
      </div>
      <Button
        disabled={disabled || isBusy}
        variant={variant}
        className={classes}
        style={style}
        {...rest}
      >
        <Space width={isBusy || icon ? '24px' : '0px'} />
        {typeof children === 'function' ? children(isBusy) : children}
      </Button>
    </div>
  );
}
